// contexts/FilterContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { customGetAPI } from "services/utils/CustomAPI";
import { useSelector, useDispatch } from "react-redux";
import { setFilterApi } from "services/Redux/userManagament";

const FilterContext = createContext();

export const useFilter = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
};

export const FilterProvider = ({
  children,
  userDetails,
  domain,
  subModuleId,
}) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [anchor, setAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [hasExistingFilter, setHasExistingFilter] = useState(false);
  const [filterApiData, setFilterApiData] = useState("");
  const dispatch = useDispatch();
  const { filterApi } = useSelector((state) => state.userReducer);
  const [callOnce, setCallOnce] = useState(true);

  const getFilterDetails = useCallback(
    async (id) => {
      const res = await customGetAPI(
        `${domain}filter-preference/?user_id=${userDetails?.id}&submodule_id=${subModuleId}`
      );
      if (res.ok) {
        setFilterApiData(res.responseDetails);
      }
    },
    [domain, subModuleId, userDetails?.id]
  );

  useEffect(() => {
    if (filterApi && subModuleId) {
      getFilterDetails();
      dispatch(setFilterApi(false));
    }
  }, [dispatch, filterApi, getFilterDetails, subModuleId]);

  useEffect(() => {
    if (filterApiData) {
      setSelectedFilters(filterApiData?.filter_condition?.table_headers || []);
      setFilterValues(filterApiData?.filter_condition?.filter_values || {});
      const hasData =
        filterApiData && Object.keys(filterApiData || {}).length > 0;
      setHasExistingFilter(hasData);
    }
  }, [filterApiData]);

  const updateFilterValues = async () => {
    const payload = {
      user_id: userDetails?.id,
      submodule_id: subModuleId,
      filter_condition: {
        table_headers: selectedFilters.map(({ icon, ...rest }) => rest),
        filter_values: filterValues,
      },
    };
    const method = hasExistingFilter ? "PUT" : "POST";
    const url = hasExistingFilter
      ? `${domain}filter-preference/${filterApiData?.id}/`
      : `${domain}filter-preference/`;

    const response = await customAPI(url, method, payload);
    if (response.ok) {
      showToast("success", "Filter values updated");
      dispatch(setFilterApi(true));
    }

    return null;
  };

  const handleResetFilters = async () => {
    if (Object.keys(filterApiData || {})?.length === 0) {
      setSelectedFilters([]);
      setFilterValues({});
      setAnchor(null);
      setActiveFilter(null);
      return;
    }

    const payload = {
      user_id: userDetails?.id,
      submodule_id: subModuleId,
      filter_condition: {
        table_headers: [],
        filter_values: {},
      },
    };

    const response = await customAPI(
      `${domain}filter-preference/${filterApiData?.id}/`,
      "PUT",
      payload
    );

    if (response.ok) {
      showToast("success", "Filter values reset");

      dispatch(setFilterApi(true));
      setSelectedFilters([]);
      setFilterValues({});
      setAnchor(null);
      setActiveFilter(null);
    }
    return null;
  };

  const value = {
    // State
    selectedFilters,
    filterValues,
    anchor,
    activeFilter,
    callOnce,

    // Setters
    setSelectedFilters,
    setFilterValues,
    setAnchor,
    setActiveFilter,
    setCallOnce,

    // Actions
    updateFilterValues,
    handleResetFilters,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};
